import { createCartMutation } from '@/api/carts/cartMutations';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/dashboard/DashboardCard';
import { MyCartsWrapper } from '@/components/dashboard/my-carts/MyCartsWrapper';
import { useFormMutation } from '@/hooks/useFormMutation';
import { routes } from '@/routes/routes';
import { createCartSchema } from '@/validation/cartValidator';
import { useNavigate } from '@tanstack/react-router';
import { useFormik } from 'formik';
import type z from 'zod';
import { toFormikValidate } from 'zod-formik-adapter';

export type CreateCartFormValues = z.infer<typeof createCartSchema>;

const initialValues: CreateCartFormValues = {
  name: '',
};

export const MyCartsCreatePage = () => {
  const navigate = useNavigate();

  const formik = useFormik<CreateCartFormValues>({
    initialValues,
    validate: toFormikValidate(createCartSchema),
    onSubmit: () => {},
  });

  const createCartMutationOptions = createCartMutation();
  const createCart = useFormMutation({
    ...createCartMutationOptions,
    formik,
    onSuccess: (...params) => {
      createCartMutationOptions.onSuccess?.(...params);
      navigate({
        to: routes.dashboard.myCarts.cart.items,
        params: {
          cartId: params[0].data.id,
        },
      });
    },
  });

  const handleSubmit = async () => {
    formik.setFieldTouched('name', true);
    const errors = await formik.validateForm();

    if (!errors.name) {
      createCart.mutate({
        name: formik.values.name,
      });
    }
  };

  return (
    <MyCartsWrapper>
      <Card>
        <CardHeader>
          <CardTitle>Create order name</CardTitle>
        </CardHeader>

        <CardContent>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
            className="flex flex-col gap-5"
          >
            <Input
              id="name"
              label="Enter subscription name"
              type="text"
              {...formik.getFieldProps('name')}
              error={
                formik.touched.name && formik.errors.name
                  ? formik.errors.name
                  : undefined
              }
              touched={!!formik.touched.name}
            />
            <div className="flex justify-between gap-4">
              <Button
                type="submit"
                disabled={createCart.isPending}
                className="w-fit"
              >
                {createCart.isPending ? 'Creating...' : 'Continue'}
              </Button>
              <Button
                type="button"
                variant="link"
                onClick={() => {
                  navigate({
                    to: routes.dashboard.myCarts.index,
                  });
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </MyCartsWrapper>
  );
};
